import { isDateExact } from '@bridebook/toolbox';
import {
  formatEventDate,
  formatEventTimeRange,
} from '@bridebook/web/lib/events/event-date-formatters';
import { sortEvents } from '@bridebook/web/lib/events/sort-unified-events';
import { isMainEvent, SimplifiedEventForDisplay } from '@bridebook/web/lib/events/types';
import { BBOrCustomSupplier } from '@bridebook/web/lib/supplier/hooks/query/use-supplier';
import {
  DATE_FORMAT,
  dateFromIDatePicker,
  format,
} from '@bridebook/web/lib/time-date-manipulation/time-date-manipulation';
import {
  useGetWeddingWebsiteDateFormat,
  WeddingWebsiteDateFormat,
} from '@bridebook/web/lib/wedding-website/hooks/use-get-wedding-website-date-format';
import { isScheduleSection } from '@bridebook/web/lib/wedding-website/utils/sections-typeguards';
import { groupBy, partition } from 'lodash';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { useWeddingWebsiteData } from '../../data/data-context';
import { MotifOrnament, MotifSectionHookProps } from './motif-section-hooks';

type SimplifiedEventForDisplayWithSupplier = SimplifiedEventForDisplay & {
  supplier: BBOrCustomSupplier | undefined;
};

const formatEventTimeRangeWithSupplier = (event: SimplifiedEventForDisplayWithSupplier) => {
  const timeRange = formatEventTimeRange(event.time, ' - ');
  return event.supplier?.name ? `${timeRange} ${event.supplier.name}` : timeRange;
};

export const MotifSchedule = (hookProps: MotifSectionHookProps) => {
  const { t } = useTranslation('weddingWebsite');
  const { events, wedding, website, mainSupplier, suppliers } = useWeddingWebsiteData();
  const section = website.sections.find(isScheduleSection);

  const [mainEventArr, restOfEventsArr] = partition(events, isMainEvent);
  const mainEvent = mainEventArr?.[0];

  const simplifiedEvents: SimplifiedEventForDisplayWithSupplier[] = useMemo(() => {
    const mainEventWithAllDataArr: SimplifiedEventForDisplayWithSupplier[] = (() => {
      if (!wedding.date || !isDateExact(wedding.date) || !mainEvent) return [];
      const date = dateFromIDatePicker(wedding.date);
      if (!date) return [];
      return [
        {
          ...mainEvent,
          date: format(date, DATE_FORMAT.DATE.ISO),
          supplier: mainSupplier,
        },
      ];
    })();

    const restOfEventsWithSupplier: SimplifiedEventForDisplayWithSupplier[] = restOfEventsArr.map(
      (event) => ({
        ...event,
        supplier: suppliers.find((supplier) => supplier.id === event.supplier),
      }),
    );

    return sortEvents([...mainEventWithAllDataArr, ...restOfEventsWithSupplier]);
  }, [mainEvent, restOfEventsArr, wedding.date, mainSupplier, suppliers]);

  const groupedEvents = groupBy(
    simplifiedEvents.filter((evt) => evt.featured),
    (ev) => ev.date,
  );

  const dateFormat = useGetWeddingWebsiteDateFormat();

  if (!section || !section.visible) return null;

  return (
    <section {...hookProps} className="bb-ww__section bb-ww__section--schedule">
      <MotifOrnament />
      <h2 className="bb-ww__section-title">{t('previewMode.tile.schedule.title')}</h2>
      {Object.keys(groupedEvents).length === 0 && (
        <p className="bb-ww__body">{t('previewMode.tile.schedule.dateTBC')}</p>
      )}
      {Object.entries(groupedEvents).map(([date, dateEvents]) => (
        <div className="bb-ww__schedule-group" key={date}>
          <h3 className="bb-ww__schedule-date">
            {formatEventDate(date, dateFormat === WeddingWebsiteDateFormat.Ordinal)}
          </h3>
          {dateEvents.map((event, eventIndex) => (
            <div className="bb-ww__schedule-event" key={eventIndex}>
              <p className="bb-ww__schedule-event-name">{event.name}</p>
              <p className="bb-ww__schedule-event-time">
                {formatEventTimeRangeWithSupplier(event)}
              </p>
              {event.description && (
                <p className="bb-ww__schedule-event-description">{event.description}</p>
              )}
            </div>
          ))}
        </div>
      ))}
    </section>
  );
};
